import { type WorkflowTrigger } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, ErrorState, Sheet, SheetContent, Skeleton } from "@sagot/ui";
import { useMemo } from "react";
import { WorkflowBuilder } from "../builder/workflow-builder";
import { definitionFrom } from "../definition-model";
import { useFormatters } from "../../shared/format";
import { useVersion } from "../use-workflows-p4";

const V = "engage.workflows.versions";
const ignore = (): void => undefined;

interface VersionSheetProps {
  workflowId: string;
  trigger: WorkflowTrigger;
  number: number;
  onClose: () => void;
}

/** A published version, read-only: the same list and canvas as the builder, with every field locked. */
export function VersionSheet({ workflowId, trigger, number, onClose }: VersionSheetProps) {
  const { t } = useTranslation();
  const format = useFormatters();
  const version = useVersion(workflowId, number);
  const definition = useMemo(() => (version.data ? definitionFrom(version.data.definition) : null), [version.data]);
  return (
    <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent
        title={t(`${V}.viewTitle`, { number })}
        description={version.data ? t(`${V}.viewMeta`, { when: format.dateTime(version.data.published_at), name: version.data.published_by_name ?? "—" }) : t(`${V}.readOnly`)}
        closeLabel={t("common.close")}
        className="max-w-3xl"
      >
        <div className="space-y-4">
          <p className="flex items-center gap-2 text-xs text-fg-muted">
            <Badge>{t(`${V}.readOnlyBadge`)}</Badge>
            {t(`${V}.readOnly`)}
          </p>
          {version.isError ? <ErrorState title={t(`${V}.viewFailed`)} onRetry={() => void version.refetch()} retryLabel={t("common.retry")} /> : null}
          {version.isPending ? <Skeleton className="h-64 rounded-md" aria-hidden="true" /> : null}
          {definition ? <WorkflowBuilder trigger={trigger} definition={definition} disabled onChange={ignore} /> : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
